import { definePreset, palette } from '@openng/optimus-ui-themes';
import { STELA_PRESET_CSS } from './stela.preset-css';
import lara from '@openng/optimus-ui-themes/lara';
import { stela2Tokens } from './stela2.tokens';

/**
 * Preset de Optimus UI para Stela 2 — alimentado únicamente por
 * `stela2.tokens.ts` (fuente única, ver ese módulo para la procedencia y la
 * regla de colisión Claude Design > Angular).
 *
 * Se mantienen los nombres `primary`/`surface` de Optimus; los nombres
 * semánticos de shadcn (`muted`, `accent`, `card`, …) no se importan.
 */
const { brand, neutral, radius, darkScheme } = stela2Tokens;

/**
 * Interpola linealmente (en RGB) entre dos colores hex. Se usa para derivar
 * la rampa `colorScheme.dark.surface` sin introducir literales de color
 * nuevos: los extremos vienen de `stela2Tokens` (blanco de Lara para el
 * texto en oscuro y `darkScheme.card`), y los pasos intermedios se calculan.
 */
function mixHex(from: string, to: string, t: number): string {
  const f = parseInt(from.slice(1), 16);
  const g = parseInt(to.slice(1), 16);
  const fr = (f >> 16) & 255,
    fg = (f >> 8) & 255,
    fb = f & 255;
  const gr = (g >> 16) & 255,
    gg = (g >> 8) & 255,
    gb = g & 255;
  const r = Math.round(fr + (gr - fr) * t);
  const gC = Math.round(fg + (gg - fg) * t);
  const b = Math.round(fb + (gb - fb) * t);
  return `#${[r, gC, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Rampa 0→900 de la superficie oscura: en Lara, `surface.0` es el valor MÁS
 * CLARO (texto/borde) y `surface.900/950` los MÁS OSCUROS (fondos) — ver
 * `dist/lara/base/index.mjs`, bloque `colorScheme.dark` (`text.color:
 * {surface.0}`, `content.background: {surface.900}`,
 * `formField.background: {surface.950}`). Se interpola linealmente desde
 * blanco (0) hasta `darkScheme.card` (900, t=1); `950` es
 * `darkScheme.background`, más oscuro que la tarjeta.
 */
const darkSurfaceWhite = '#ffffff';
const darkSurfaceRampSteps: Array<[number, number]> = [
  [0, 0],
  [50, 1],
  [100, 2],
  [200, 3],
  [300, 4],
  [400, 5],
  [500, 6],
  [600, 7],
  [700, 8],
  [800, 9],
  [900, 10],
];
const darkSurface: Record<number, string> = Object.fromEntries(
  darkSurfaceRampSteps.map(([step, index]) => [
    step,
    index / 10 === 1 ? darkScheme.card : mixHex(darkSurfaceWhite, darkScheme.card, index / 10),
  ]),
);
darkSurface[950] = darkScheme.background;


/**
 * Esquema oscuro de los componentes (ITSM #1839544). Antes lo pintaba un
 * puente de 38 reglas `body.dark-theme .p-* { … !important }` en
 * theme.css, porque Lara define estos valores dentro de `colorScheme.dark` y
 * pisaba los de nivel superior del preset. Se trasladan aquí tal cual: el
 * aspecto en oscuro no cambia (decisión del usuario, 1-oct-2026). Los
 * `var(--stela-*)` se resuelven en <html>, donde `html[data-bs-theme="dark"]`
 * (theme-kit.tokens.css) ya tiene los valores oscuros.
 */
const surface = 'var(--stela-bg-surface)';
const sunken = 'var(--stela-bg-app)';
const muted = 'var(--stela-bg-muted)';
const border = 'var(--stela-border-color)';
const text = 'var(--stela-text-primary)';
const textSecondary = 'var(--stela-text-secondary)';
const block = { background: surface, borderColor: border, color: text };
const field = {
  background: sunken,
  borderColor: border,
  color: text,
  disabledBackground: muted,
  disabledColor: 'var(--stela-text-muted)',
};
const toastMessage = { background: surface, borderColor: border, color: text, detailColor: textSecondary };
const menuDark = {
  root: block,
  item: { color: text, focusBackground: muted, focusColor: text, icon: { color: textSecondary, focusColor: textSecondary } },
};
const tableDark = {
  header: block,
  headerCell: { background: muted, borderColor: border, color: textSecondary },
  // Las filas alternas también en superficie: Lara las oscurece en oscuro.
  row: { background: surface, stripedBackground: surface, hoverBackground: muted, color: text },
  bodyCell: { borderColor: border },
  footer: block,
  footerCell: block,
};
// El puente de treetable solo cubría cabecera, filas y pie (no las celdas).
const treeTableDark = {
  header: block,
  row: { background: surface, hoverBackground: muted, color: text },
  footer: block,
};

const stelaDarkScheme = {
  components: {
    panel: { colorScheme: { dark: { root: block, header: { background: surface, borderColor: border, color: text } } } },
    inputtext: { colorScheme: { dark: { root: field } } },
    textarea: { colorScheme: { dark: { root: field } } },
    select: { colorScheme: { dark: { root: { background: sunken, borderColor: border, color: text }, overlay: block, option: { focusBackground: muted } } } },
    // Sin `color` en la raíz: la etiqueta del multiselect conserva el suyo, como con el puente.
    multiselect: { colorScheme: { dark: { root: { background: sunken, borderColor: border }, overlay: block, option: { focusBackground: muted } } } },
    checkbox: { colorScheme: { dark: { root: { background: sunken, borderColor: border }, icon: { checkedColor: 'var(--stela-navy)' } } } },
    // La cabecera de columna usa el color apagado en claro (antes lo ponía theme.css).
    datatable: { colorScheme: { light: { headerCell: { color: 'var(--stela-text-muted)' } }, dark: tableDark } },
    treetable: { colorScheme: { dark: treeTableDark } },
    paginator: { colorScheme: { dark: { root: { background: surface, color: textSecondary }, navButton: { color: textSecondary, hoverBackground: muted, hoverColor: text, selectedColor: textSecondary } } } },
    tree: { colorScheme: { dark: { node: { hoverBackground: muted, color: text } } } },
    datepicker: { colorScheme: { dark: { panel: block, header: block, date: { hoverBackground: muted } } } },
    accordion: { colorScheme: { dark: { content: block } } },
    dialog: { colorScheme: { dark: { root: block } } },
    tooltip: { colorScheme: { dark: { root: { background: 'var(--stela-bg-elevated)', color: text } } } },
    toast: { colorScheme: { dark: { info: toastMessage, success: toastMessage, warn: toastMessage, error: toastMessage, secondary: toastMessage, contrast: toastMessage } } },
    menu: { colorScheme: { dark: menuDark } },
    contextmenu: { colorScheme: { dark: menuDark } },
  },
};

export const StelaPreset = definePreset(lara, {
  primitive: {
    // palette() interpola tint/shade desde el hex de origen y normaliza a
    // minúsculas; se fija el escalón 500 al valor exacto del token de marca.
    gold: Object.assign({}, palette(brand.gold), { 500: brand.gold }),
    navy: Object.assign({}, palette(brand.navy), { 500: brand.navy }),
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: radius.sm,
      md: radius.md,
      lg: radius.lg,
      xl: radius.xl,
    },
  },
  semantic: {
    primary: {
      50: '{gold.50}',
      100: '{gold.100}',
      200: '{gold.200}',
      300: '{gold.300}',
      400: '{gold.400}',
      500: '{gold.500}',
      600: '{gold.600}',
      700: '{gold.700}',
      800: '{gold.800}',
      900: '{gold.900}',
      950: '{gold.950}',
    },
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
    },
    // Lote B (Tarea 4, ITSM #1839381): --p-form-field-* movido desde
    // theme-kit.prime-overrides.css. Son valores agnósticos de esquema — el
    // puente solo los declaraba una vez, bajo :root, sin override propio en
    // body.dark-theme — así que quedan al nivel agnóstico de `formField`
    // (Lara define paddingX/paddingY/sm aquí sin depender de colorScheme).
    formField: {
      paddingX: '0.6rem',
      paddingY: '0.4rem',
      sm: {
        paddingX: '0.5rem',
        paddingY: '0.3rem',
      },
    },
    // Lote E (Tarea 4, ITSM #1839381): --p-content-border-radius y
    // --p-overlay-modal-shadow son agnósticos de esquema en el puente
    // (una sola declaración en :root) y Lara los define a nivel superior
    // de `semantic` (sin colorScheme): dist/lara/base `content.borderRadius`
    // y `overlay.modal.shadow`.
    content: {
      borderRadius: 'var(--stela-radius-sm)',
    },
    overlay: {
      modal: {
        shadow: 'var(--stela-shadow-lg)',
      },
    },
    colorScheme: {
      light: {
        // Lote E (Tarea 4, ITSM #1839381): --p-primary-active-color coincide
        // con --p-primary-hover-color en el puente (ambos var(--stela-gold-dark)),
        // así que se fija literal en vez del `{primary.700}` derivado de la
        // paleta, que no coincidía exactamente.
        primary: {
          color: '{primary.500}',
          contrastColor: brand.aubergineDark,
          hoverColor: brand.goldDark,
          activeColor: brand.goldDark,
        },
        // --p-form-field-icon-color / --p-iconfield-icon-color (Lara's
        // `iconfield.icon.color` referencia `{form.field.icon.color}`, así
        // que basta con fijarlo aquí). Agnóstico de esquema en el puente
        // original (sin override de oscuro), duplicado igual en dark abajo.
        formField: {
          iconColor: 'var(--stela-fg-3)',
        },
        text: {
          color: 'var(--stela-text-primary)',
          mutedColor: 'var(--stela-text-muted)',
        },
        content: {
          borderColor: 'var(--stela-line)',
        },
        surface: {
          0: '#ffffff',
          50: neutral.surfaceSubtle,
          100: neutral.canvas,
          200: neutral.muted,
          300: neutral.line,
          400: neutral.fg4,
          500: neutral.fg3,
          600: neutral.fg2,
          700: neutral.fg,
          800: brand.navyLight,
          900: brand.navy,
          950: brand.navyDark,
        },
      },
      dark: {
        // El texto sobre el dorado es el mismo en ambos esquemas: berenjena
        // oscuro (6,94:1 sobre gold, 5,31:1 sobre gold-dark), en vez de
        // darkScheme.primaryForeground.
        primary: {
          color: '{primary.500}',
          contrastColor: brand.aubergineDark,
          hoverColor: brand.goldDark,
          activeColor: brand.goldDark,
        },
        formField: {
          iconColor: 'var(--stela-fg-3)',
        },
        // Lote E: --p-text-color / --p-text-muted-color — agnósticos de
        // esquema en el puente (una sola declaración en :root); se repiten
        // en oscuro porque las var(--stela-text-*) ya cambian de valor bajo
        // body.dark-theme.
        text: {
          color: 'var(--stela-text-primary)',
          mutedColor: 'var(--stela-text-muted)',
        },
        content: {
          borderColor: 'var(--stela-line)',
        },
        surface: {
          0: darkSurface[0],
          50: darkSurface[50],
          100: darkSurface[100],
          200: darkSurface[200],
          300: darkSurface[300],
          400: darkSurface[400],
          500: darkSurface[500],
          600: darkSurface[600],
          700: darkSurface[700],
          800: darkSurface[800],
          900: darkSurface[900],
          950: darkSurface[950],
        },
      },
    },
  },
  components: {
    // Lara anima el spinner en rosa, azul, verde y ámbar (dentro de colorScheme,
    // así que hay que pisarlo ahí). En Stela va siempre en el color primario.
    progressspinner: {
      colorScheme: {
        light: { root: { colorOne: '{primary.color}', colorTwo: '{primary.color}', colorThree: '{primary.color}', colorFour: '{primary.color}' } },
        dark: { root: { colorOne: '{primary.color}', colorTwo: '{primary.color}', colorThree: '{primary.color}', colorFour: '{primary.color}' } },
      },
    },
    // Lote A (Tarea 4, ITSM #1839381): puente --p-button-* movido desde
    // theme-kit.prime-overrides.css y theme.css. Los valores son los que ya
    // ganaban en cascada (theme.css importa theme-kit.css y luego redefine
    // las mismas variables en su propio :root/body.dark-theme, así que sus
    // declaraciones pisaban a las de prime-overrides para primary/secondary/
    // danger). Se dejan como var(--stela-*) tal cual (regla 2 del brief) para
    // conservar los skins y el cambio de esquema que vive en CSS.
    button: {
      root: {
        borderRadius: 'var(--stela-radius-sm)',
        paddingX: '10px',
        paddingY: '5px',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: 'var(--stela-color-primary)',
              hoverBackground: 'color-mix(in srgb, var(--stela-color-primary) 85%, #000)',
              activeBackground: 'color-mix(in srgb, var(--stela-color-primary) 75%, #000)',
              borderColor: 'var(--stela-color-primary)',
              hoverBorderColor: 'color-mix(in srgb, var(--stela-color-primary) 85%, #000)',
              activeBorderColor: 'color-mix(in srgb, var(--stela-color-primary) 75%, #000)',
              color: 'var(--stela-text-on-primary)',
              hoverColor: 'var(--stela-text-on-primary)',
              activeColor: 'var(--stela-text-on-primary)',
            },
            secondary: {
              background: 'var(--stela-bg-surface)',
              hoverBackground: 'var(--stela-bg-muted)',
              activeBackground: 'var(--stela-bg-muted)',
              borderColor: 'var(--stela-border-color)',
              hoverBorderColor: 'var(--stela-border-color)',
              activeBorderColor: 'var(--stela-border-color)',
              color: 'var(--stela-text-primary)',
              hoverColor: 'var(--stela-text-primary)',
              activeColor: 'var(--stela-text-primary)',
            },
            danger: {
              background: 'var(--stela-color-danger)',
              hoverBackground: 'color-mix(in srgb, var(--stela-color-danger) 85%, #000)',
              activeBackground: 'color-mix(in srgb, var(--stela-color-danger) 75%, #000)',
              borderColor: 'var(--stela-color-danger)',
              hoverBorderColor: 'color-mix(in srgb, var(--stela-color-danger) 85%, #000)',
              activeBorderColor: 'color-mix(in srgb, var(--stela-color-danger) 75%, #000)',
              color: '#fff',
              activeColor: '#fff',
            },
          },
          // Secundario con contorno (`[outlined]="true"`): sin relleno, el borde por defecto de Lara
          // (`surface.200`, #EEF0F3) casi no se distingue del fondo de página; se sube a un tono visible
          // que en oscuro cambia solo (`--stela-fg-4`).
          outlined: {
            secondary: {
              borderColor: 'var(--stela-border-color-strong)',
            },
          },
        },
        dark: {
          root: {
            // primary/danger no tenían override bajo body.dark-theme en el
            // puente original: sus reglas vivían en :root (theme.css) y por
            // tanto se aplicaban también en oscuro (:root no depende del
            // esquema). Se duplican aquí para que sigan aplicando en oscuro;
            // solo secondary tenía valores propios de oscuro que difieren.
            primary: {
              background: 'var(--stela-color-primary)',
              hoverBackground: 'color-mix(in srgb, var(--stela-color-primary) 85%, #000)',
              activeBackground: 'color-mix(in srgb, var(--stela-color-primary) 75%, #000)',
              borderColor: 'var(--stela-color-primary)',
              hoverBorderColor: 'color-mix(in srgb, var(--stela-color-primary) 85%, #000)',
              activeBorderColor: 'color-mix(in srgb, var(--stela-color-primary) 75%, #000)',
              color: 'var(--stela-text-on-primary)',
              hoverColor: 'var(--stela-text-on-primary)',
              activeColor: 'var(--stela-text-on-primary)',
            },
            secondary: {
              background: 'var(--stela-bg-surface)',
              hoverBackground: 'var(--stela-bg-muted)',
              activeBackground: 'var(--stela-bg-muted)',
              borderColor: 'var(--stela-border-color)',
              hoverBorderColor: 'var(--stela-border-color)',
              activeBorderColor: 'var(--stela-border-color)',
              color: 'var(--stela-text-primary)',
              hoverColor: 'var(--stela-text-primary)',
              activeColor: 'var(--stela-text-primary)',
            },
            danger: {
              background: 'var(--stela-color-danger)',
              hoverBackground: 'color-mix(in srgb, var(--stela-color-danger) 85%, #000)',
              activeBackground: 'color-mix(in srgb, var(--stela-color-danger) 75%, #000)',
              borderColor: 'var(--stela-color-danger)',
              hoverBorderColor: 'color-mix(in srgb, var(--stela-color-danger) 85%, #000)',
              activeBorderColor: 'color-mix(in srgb, var(--stela-color-danger) 75%, #000)',
              color: '#fff',
              activeColor: '#fff',
            },
          },
          // Secundario con contorno (`[outlined]="true"`): sin relleno, el borde por defecto de Lara
          // (`surface.200`, #EEF0F3) casi no se distingue del fondo de página; se sube a un tono visible
          // que en oscuro cambia solo (`--stela-fg-4`).
          outlined: {
            secondary: {
              borderColor: 'var(--stela-border-color-strong)',
            },
          },
        },
      },
    },

    // Lote B (Tarea 4, ITSM #1839381): puente --p-inputtext-*, --p-textarea-*
    // movido desde theme-kit.prime-overrides.css (claro) y theme.css
    // (body.dark-theme, oscuro). focusBorderColor y borderRadius no tenían
    // override propio de oscuro en el puente (solo vivían en :root), así que
    // se duplican en dark para no perderlos (mismo criterio que lote A).
    inputtext: {
      // borderRadius y focusBorderColor van en `root` (agnóstico de esquema,
      // no bajo `colorScheme`): Lara resuelve ese token vía
      // `{form.field.border.radius}` -> `{border.radius.md}` cuando no hay
      // override a nivel de `root`; un valor puesto solo dentro de
      // `colorScheme.light/dark.root` NO lo pisa (verificado en vivo con
      // `ng serve`: quedaba en 6px, el radio md por defecto, en vez de los
      // 4px de --stela-radius-sm). Ver fix round 1 de lote B.
      root: {
        focusBorderColor: 'var(--stela-gold)',
        borderRadius: 'var(--stela-radius-sm)',
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-line)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-bg-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-border-color)',
          },
        },
      },
    },
    textarea: {
      // borderRadius agnóstico de esquema — ver la nota en `inputtext` (fix
      // round 1 de lote B): va en `root`, no bajo `colorScheme`.
      root: {
        borderRadius: 'var(--stela-radius-sm)',
      },
      colorScheme: {
        light: {
          root: {
            color: 'var(--stela-text-primary)',
            // Mismo fondo que el input de texto normal (más oscuro) para
            // consistencia visual — comentario original del puente.
            background: 'var(--stela-surface)',
            borderColor: 'var(--stela-line)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-bg-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-border-color)',
          },
        },
      },
    },

    // --p-select-* / --p-multiselect-*: el puente solo definía select en
    // claro (theme-kit.prime-overrides.css). theme.css (body.dark-theme)
    // también declaraba overlay/option de select con !important, y todo
    // multiselect (root+overlay+option), pero el controlador (fix round 2)
    // confirmó por captura real (ng build + Playwright, computed --p-* en
    // /theme-kit/*) que esas reglas de body.dark-theme NUNCA ganaban en el
    // navegador: en oscuro, la app real siempre mostró los valores por
    // defecto de oscuro de Lara para select.overlay/select.option y para
    // multiselect entero (p.ej. --p-select-overlay-background computaba
    // #141E34 — el surface.900 oscuro de Lara — nunca el #2A1E2E de
    // var(--stela-bg-surface) que pedía el bridge). Fidelidad con la
    // baseline (c586b36d6) exige reproducir ese resultado, no la intención
    // del CSS fuente: por eso select.colorScheme.dark NO lleva overlay ni
    // option, y multiselect no lleva colorScheme.dark en absoluto (Lara
    // aporta light y dark igual que hacía en la baseline real). Solo
    // select.colorScheme.dark.root (background/color/borderColor) sí ganaba
    // en la baseline y se conserva.
    select: {
      // borderRadius agnóstico de esquema — ver la nota en `inputtext` (fix
      // round 1 de lote B): va en `root`, no bajo `colorScheme`.
      root: {
        borderRadius: 'var(--stela-radius-sm)',
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-line)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-bg-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-border-color)',
          },
        },
      },
    },
    // multiselect: el puente NO tenía ningún valor vivo en claro (Lara por
    // defecto) y su bloque de oscuro (body.dark-theme, theme.css) nunca
    // llegó a aplicarse en la app real — ver nota arriba en `select`. No se
    // declara ningún override: Lara resuelve claro y oscuro con sus propios
    // valores, igual que en la baseline.

    // --p-autocomplete-*: el puente solo lo definía una vez, bajo :root
    // (theme-kit.prime-overrides.css), sin ningún override de oscuro en
    // theme.css. Es agnóstico de esquema entero. `dropdown` y `chip` están
    // definidos por Lara bajo `colorScheme` (ver dist/lara/autocomplete), así
    // que hay que duplicarlos ahí para pisar el valor por esquema de Lara;
    // `root`/`overlay`/`option`/`optionGroup` no son per-scheme en Lara y
    // basta con ponerlos una vez, pero se replican igual en ambos para que
    // el test de fidelidad (claro == oscuro) sea explícito.
    autocomplete: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-line)',
            hoverBorderColor: 'var(--stela-line)',
            focusBorderColor: 'var(--stela-gold)',
            borderRadius: 'var(--stela-radius-sm)',
            placeholderColor: 'var(--stela-text-muted)',
            disabledBackground: 'var(--stela-bg-muted)',
            disabledColor: 'var(--stela-fg-3)',
          },
          overlay: {
            background: 'var(--stela-surface)',
            borderColor: 'var(--stela-line)',
            color: 'var(--stela-text-primary)',
          },
          option: {
            color: 'var(--stela-text-primary)',
            focusBackground: 'var(--stela-bg-muted)',
            focusColor: 'var(--stela-fg-1)',
            // rgba fija (no hex) que reproduce el "dorado al 12%" usado en
            // varios sitios del puente (p.ej. menu/tree, fuera de este
            // lote); se mantiene literal, igual que en el origen.
            selectedBackground: 'rgba(201, 168, 76, 0.12)',
            selectedColor: 'var(--stela-gold-dark)',
          },
          optionGroup: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-2)',
          },
          chip: {
            focusBackground: 'var(--stela-bg-muted)',
            focusColor: 'var(--stela-fg-1)',
          },
          dropdown: {
            background: 'var(--stela-bg-muted)',
            hoverBackground: 'var(--stela-line)',
            activeBackground: 'var(--stela-line)',
            color: 'var(--stela-fg-2)',
            hoverColor: 'var(--stela-fg-1)',
            borderColor: 'var(--stela-line)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-text-primary)',
            borderColor: 'var(--stela-line)',
            hoverBorderColor: 'var(--stela-line)',
            focusBorderColor: 'var(--stela-gold)',
            borderRadius: 'var(--stela-radius-sm)',
            placeholderColor: 'var(--stela-text-muted)',
            disabledBackground: 'var(--stela-bg-muted)',
            disabledColor: 'var(--stela-fg-3)',
          },
          overlay: {
            background: 'var(--stela-surface)',
            borderColor: 'var(--stela-line)',
            color: 'var(--stela-text-primary)',
          },
          option: {
            color: 'var(--stela-text-primary)',
            focusBackground: 'var(--stela-bg-muted)',
            focusColor: 'var(--stela-fg-1)',
            selectedBackground: 'rgba(201, 168, 76, 0.12)',
            selectedColor: 'var(--stela-gold-dark)',
          },
          optionGroup: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-2)',
          },
          chip: {
            focusBackground: 'var(--stela-bg-muted)',
            focusColor: 'var(--stela-fg-1)',
          },
          dropdown: {
            background: 'var(--stela-bg-muted)',
            hoverBackground: 'var(--stela-line)',
            activeBackground: 'var(--stela-line)',
            color: 'var(--stela-fg-2)',
            hoverColor: 'var(--stela-fg-1)',
            borderColor: 'var(--stela-line)',
          },
        },
      },
    },

    // --p-checkbox-* / --p-radiobutton-*: agnósticos de esquema en el
    // puente (solo :root, sin override de oscuro). `checkedBackground`,
    // `checkedBorderColor` y `borderRadius` SÍ están en el `root` de nivel
    // superior en Lara (no bajo colorScheme), así que un único valor basta
    // para ambos esquemas.
    checkbox: {
      root: {
        checkedBackground: 'var(--stela-gold)',
        checkedHoverBackground: 'var(--stela-gold-dark)',
        checkedBorderColor: 'var(--stela-gold)',
        borderRadius: 'var(--stela-radius-sm)',
      },
      icon: {
        checkedColor: 'var(--stela-aubergine-dark)',
      },
    },
    radiobutton: {
      root: {
        checkedBackground: 'var(--stela-gold)',
        checkedBorderColor: 'var(--stela-gold)',
      },
      icon: {
        checkedColor: 'var(--stela-aubergine-dark)',
      },
    },
    // --p-toggleswitch-*: agnóstico de esquema en el puente.
    // `checkedBorderColor` está en el `root` de nivel superior en Lara, pero
    // `checkedBackground`/`checkedHoverBackground` (root) y
    // `checkedBackground` (handle) solo existen dentro de `colorScheme` en
    // Lara (ver dist/lara/toggleswitch) — hay que duplicarlos en claro/
    // oscuro para pisar el valor propio de cada esquema.
    toggleswitch: {
      root: {
        checkedBorderColor: 'var(--stela-gold)',
      },
      colorScheme: {
        light: {
          root: {
            checkedBackground: 'var(--stela-gold)',
            checkedHoverBackground: 'var(--stela-gold-dark)',
          },
          handle: {
            checkedBackground: '#ffffff',
          },
        },
        dark: {
          root: {
            checkedBackground: 'var(--stela-gold)',
            checkedHoverBackground: 'var(--stela-gold-dark)',
          },
          handle: {
            checkedBackground: '#ffffff',
          },
        },
      },
    },

    // --p-datatable-*: solo 4 variables vivas en el puente (las de
    // theme.css están dentro de un bloque `/* ... */` comentado desde antes
    // de esta tarea — nunca se aplicaron, se dejan tal cual en el CSS).
    // `row.background`, `bodyCell.borderColor` y `headerCell.borderColor`
    // son propiedades de nivel superior en Lara (dist/lara/datatable), así
    // que un único valor basta para ambos esquemas. `header.background`
    // solo existe bajo `colorScheme` en Lara; el puente lo recalculaba en
    // `body.dark-theme` con el mismo `var(--stela-bg-muted)`, así que claro
    // y oscuro llevan el mismo valor.
    datatable: {
      row: {
        background: 'var(--stela-surface)',
      },
      bodyCell: {
        borderColor: 'var(--stela-line)',
      },
      headerCell: {
        borderColor: 'var(--stela-line)',
      },
      colorScheme: {
        light: {
          header: {
            background: 'var(--stela-bg-muted)',
          },
        },
        dark: {
          header: {
            background: 'var(--stela-bg-muted)',
          },
        },
      },
    },

    // --p-treetable-*: en Lara (dist/lara/treetable), `header`/`headerCell`/
    // `footerCell` solo exponen `color`/`background` bajo `colorScheme`
    // (sus objetos de nivel superior solo traen `borderColor`/`padding`/
    // `gap`); `row`, `bodyCell` y `nodeToggleButton` sí están en el nivel
    // superior. El puente solo recalculaba `header-background` y
    // `header-cell-background` en `body.dark-theme` (con el mismo
    // `var(--stela-bg-muted)`); el resto de variables solo vivían en
    // `:root`, así que se repiten igual en `colorScheme.light` y `.dark`
    // (heredan el esquema activo a través del propio `var(--stela-*)`).
    // `--p-treetable-header-color` no tiene análogo top-level en Lara: solo
    // existe dentro de `colorScheme.header.color`, de ahí que vaya en el
    // bloque de colorScheme y no en `header` de nivel superior.
    treetable: {
      header: {
        borderColor: 'var(--stela-line)',
      },
      headerCell: {
        borderColor: 'var(--stela-line)',
      },
      row: {
        background: 'var(--stela-surface)',
        color: 'var(--stela-fg-1)',
        hoverBackground: 'var(--stela-bg)',
        hoverColor: 'var(--stela-fg-1)',
      },
      bodyCell: {
        borderColor: 'var(--stela-line)',
      },
      footerCell: {
        borderColor: 'var(--stela-line)',
      },
      nodeToggleButton: {
        color: 'var(--stela-fg-3)',
        hoverColor: 'var(--stela-gold)',
      },
      colorScheme: {
        light: {
          header: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-1)',
          },
          headerCell: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-2)',
            hoverBackground: 'var(--stela-bg)',
          },
          footerCell: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-fg-2)',
          },
        },
        dark: {
          header: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-1)',
          },
          headerCell: {
            background: 'var(--stela-bg-muted)',
            color: 'var(--stela-fg-2)',
            hoverBackground: 'var(--stela-bg)',
          },
          footerCell: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-fg-2)',
          },
        },
      },
    },

    // --p-paginator-*: a diferencia de datatable/treetable/tree, el
    // `paginator` de Lara (dist/lara/paginator) NO tiene bloque
    // `colorScheme` en absoluto — todas sus propiedades (root, navButton,
    // currentPageReport, jumpToPageInput) son de nivel superior. `root` no
    // tiene `borderColor` (solo padding/gap/borderRadius/background/color),
    // así que `--p-paginator-border-color` del puente no reproducía nada —
    // se descarta (regla 6). `navButton.selectedBackground` es la misma
    // rgba fija del "dorado al 12%" que aparece en autocomplete/menu/tree
    // (ver lote B); se mantiene literal. `hoverBackground` se recalculaba en
    // `body.dark-theme` con el mismo `var(--stela-bg-muted)`, así que un
    // único valor de nivel superior basta (el esquema lo resuelve el propio
    // --stela-bg-muted al heredar).
    paginator: {
      root: {
        background: 'var(--stela-surface)',
        color: 'var(--stela-fg-2)',
        padding: 'var(--stela-space-2) var(--stela-space-3)',
        gap: 'var(--stela-space-1)',
        borderRadius: 'var(--stela-radius-sm)',
      },
      navButton: {
        background: 'transparent',
        hoverBackground: 'var(--stela-bg-muted)',
        selectedBackground: 'rgba(201, 168, 76, 0.12)',
        color: 'var(--stela-fg-2)',
        hoverColor: 'var(--stela-fg-1)',
        selectedColor: 'var(--stela-gold-dark)',
        borderRadius: 'var(--stela-radius-sm)',
        width: '28px',
        height: '28px',
      },
    },

    // --p-tree-*: `root.background` y todo `node.*` son propiedades de
    // nivel superior en Lara (dist/lara/tree), sin override de oscuro en el
    // puente, así que un único valor basta para ambos esquemas.
    // `selectedBackground` reutiliza la misma rgba fija del "dorado al 12%"
    // que autocomplete/paginator (lote B).
    tree: {
      root: {
        background: 'var(--stela-surface)',
      },
      node: {
        hoverBackground: 'var(--stela-bg)',
        selectedBackground: 'rgba(201, 168, 76, 0.12)',
        selectedColor: 'var(--stela-gold-dark)',
        color: 'var(--stela-fg-1)',
      },
    },

    // --p-panel-*: theme.css importa theme-kit.css (con prime-overrides) y
    // luego redefine background/borderColor en su propio :root, así que sus
    // valores (var(--stela-bg-surface) / var(--stela-border-color)) son los
    // que ganaban sobre los de prime-overrides (var(--stela-surface) /
    // var(--stela-line)). `header.background` SOLO existe bajo
    // `colorScheme` en Lara (dist/lara/panel) — top-level no tiene
    // `background` — así que va en colorScheme.{light,dark}.
    //
    // FIX (ronda 1 de revisión): el override de `body.dark-theme` para
    // `--p-panel-header-background` (var(--stela-bg-surface)) nunca ganaba
    // en el build real — verificado con `inspect_dark2.mjs` contra el build
    // de la línea base (ff80f4ca2): con `body.dark-theme` activo, el valor
    // computado de `--p-panel-header-background` en TODAS las rutas de
    // `/theme-kit/*` (no solo advanced/ui-primitives) seguía siendo
    // `#F9FAFB` (var(--stela-bg-soft), el valor de :root), nunca
    // `var(--stela-bg-surface)`. Así que el valor "resuelto" por el puente
    // en oscuro era, de hecho, el mismo que en claro; colorScheme.dark
    // reproduce ESE resultado (var(--stela-bg-soft)), no la declaración de
    // `body.dark-theme` que en la práctica no aplicaba.
    // El resto (root.background/borderColor, header.borderColor/
    // borderWidth/padding, toggleableHeader.padding) es de nivel superior y
    // usa el mismo var en claro y oscuro (body.dark-theme repetía la misma
    // referencia). `--p-panel-header-height` no existe en Lara (header no
    // tiene esa clave) — se descarta (regla 6), no hacía nada.
    panel: {
      // Bloque del prototipo Stela 2 (.card de styles.css): radio lg, cabecera
      // blanca separada por una línea, rellenos 16/20. La tipografía del
      // título (Playfair) no tiene token en Lara: vive en theme.css.
      root: {
        background: 'var(--stela-bg-surface)',
        borderColor: 'var(--stela-border-color)',
        borderRadius: 'var(--stela-radius-lg)',
      },
      header: {
        borderWidth: '0 0 1px 0',
        borderColor: 'var(--stela-border-color)',
        padding: '16px 20px',
        borderRadius: 'calc(var(--stela-radius-lg) - 1px) calc(var(--stela-radius-lg) - 1px) 0 0',
      },
      toggleableHeader: {
        padding: '10px 20px',
      },
      title: {
        fontWeight: '600',
      },
      content: {
        padding: '20px',
      },
      colorScheme: {
        light: {
          header: {
            background: 'var(--stela-bg-surface)',
            color: 'var(--stela-fg-1)',
          },
        },
        dark: {
          header: {
            background: 'var(--stela-bg-surface)',
            color: 'var(--stela-fg-1)',
          },
        },
      },
    },

    // --p-card-*: card no tiene bloque `colorScheme` en Lara (dist/lara/card)
    // — todo `root` es de nivel superior. Radio y título como el bloque
    // (.card del prototipo); el borde no tiene token y va en theme.css.
    card: {
      root: {
        background: 'var(--stela-surface)',
        borderRadius: 'var(--stela-radius-lg)',
        shadow: 'var(--stela-shadow-sm)',
      },
      body: {
        padding: '20px',
      },
      title: {
        fontSize: '16px',
        fontWeight: '600',
      },
    },

    // --p-tabs-*: `tab.background/hoverBackground/activeBackground` SOLO
    // existen bajo `colorScheme` en Lara (dist/lara/tabs) — el objeto `tab`
    // de nivel superior no tiene `background` — así que van en
    // colorScheme.{light,dark}. `tab.hoverBackground` fue redefinido en
    // theme.css (:root y body.dark-theme) a var(--stela-bg-muted), que gana
    // sobre var(--stela-bg) de prime-overrides en ambos esquemas.
    // `tab.activeBorderColor` SÍ es de nivel superior en Lara, pero difiere
    // entre esquemas: body.dark-theme lo recalculaba a
    // var(--stela-border-color) en vez de var(--stela-gold) de
    // prime-overrides/​:root, así que también va en colorScheme.
    // `tab.activeColor`, `tablist.borderColor`, `tabpanel.background/color`
    // y `activeBar.background` son agnósticos de esquema (theme.css repite
    // el mismo valor en body.dark-theme, o no los toca). `tab.fontWeight`
    // solo vive en theme.css :root (600), sin override en oscuro.
    //
    // FIX (ronda 1 de revisión): `tab.activeBorderColor` se había puesto en
    // colorScheme.dark con var(--stela-border-color) porque
    // `body.dark-theme` lo recalculaba así en el CSS — pero, igual que con
    // panel.header.background, ese override de `body.dark-theme` nunca
    // ganaba en el build real. Verificado con `inspect_dark2.mjs` contra el
    // build de la línea base (ff80f4ca2): con `body.dark-theme` activo, el
    // valor computado de `--p-tabs-tab-active-border-color` en TODAS las
    // rutas de `/theme-kit/*` seguía siendo `#C9A84C`
    // (var(--stela-gold), el valor de :root de prime-overrides), nunca
    // var(--stela-border-color). Es decir, el puente resolvía el mismo
    // dorado en claro y en oscuro; `tab.activeBorderColor` vuelve a ser
    // agnóstico de esquema (nivel superior en Lara, sin necesidad de
    // colorScheme) con ese único valor.
    // Pestañas compactas con subrayado dorado (antes con !important en
    // theme.css, ITSM #1839544): texto secundario, el activo en texto
    // principal, sin fondo en ningún estado. El 13,5px y el 600 del activo no
    // tienen token y siguen en theme.css.
    tabs: {
      tab: {
        fontWeight: '500',
        color: 'var(--stela-text-secondary)',
        hoverColor: 'var(--stela-text-primary)',
        activeColor: 'var(--stela-text-primary)',
        padding: '10px 14px',
        gap: '6px',
        borderWidth: '0 0 2px 0',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'var(--stela-gold)',
      },
      tablist: {
        background: 'transparent',
        borderColor: 'var(--stela-border-color)',
      },
      tabpanel: {
        background: 'var(--stela-surface)',
        color: 'var(--stela-text-primary)',
      },
      activeBar: {
        background: 'var(--stela-gold)',
      },
      colorScheme: {
        light: {
          tab: {
            background: 'transparent',
            hoverBackground: 'transparent',
            activeBackground: 'transparent',
          },
        },
        dark: {
          tab: {
            background: 'transparent',
            hoverBackground: 'transparent',
            activeBackground: 'transparent',
          },
        },
      },
    },

    // --p-accordion-*: `header.background/hoverBackground/activeBackground`
    // SOLO existen bajo `colorScheme` en Lara (dist/lara/accordion) — igual
    // que en tabs, el objeto `header` de nivel superior no tiene
    // `background` — así que van en colorScheme.{light,dark} (mismo valor
    // en ambos: el puente solo vivía en prime-overrides, sin override en
    // body.dark-theme). `panel.borderColor`, `header.color/activeColor` y
    // `content.*` son de nivel superior.
    accordion: {
      panel: {
        borderColor: 'var(--stela-line)',
      },
      header: {
        color: 'var(--stela-fg-1)',
        activeColor: 'var(--stela-fg-1)',
      },
      content: {
        background: 'var(--stela-surface)',
        color: 'var(--stela-text-primary)',
        borderColor: 'var(--stela-line)',
      },
      colorScheme: {
        light: {
          header: {
            background: 'var(--stela-bg-muted)',
            hoverBackground: 'var(--stela-bg)',
            activeBackground: 'var(--stela-surface)',
          },
        },
        dark: {
          header: {
            background: 'var(--stela-bg-muted)',
            hoverBackground: 'var(--stela-bg)',
            activeBackground: 'var(--stela-surface)',
          },
        },
      },
    },

    // --p-toolbar-*: `root.background/color` SÍ tienen override propio bajo
    // `colorScheme` en Lara (dist/lara/toolbar: `{surface.50}`/`{surface.800}`
    // para background, `{content.color}` para color), así que hay que fijar
    // ambos esquemas para que el valor del puente gane sobre el de Lara.
    // `root.borderColor/borderRadius` son de nivel superior sin override, un
    // único valor basta. El puente solo vivía en prime-overrides (theme.css
    // no toca toolbar).
    toolbar: {
      root: {
        borderColor: 'var(--stela-line)',
        borderRadius: 'var(--stela-radius-md)',
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-fg-1)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-surface)',
            color: 'var(--stela-fg-1)',
          },
        },
      },
    },

    // --p-tieredmenu-*: el menú de usuario de la barra superior (único uso)
    // declaraba estas variables en su CSS con !important; viven aquí.
    tieredmenu: {
      root: {
        background: 'var(--stela-surface)',
        borderColor: 'var(--stela-line)',
        color: 'var(--stela-fg)',
        borderRadius: 'var(--stela-radius-md)',
        shadow: 'var(--stela-shadow-lg)',
      },
      item: {
        color: 'var(--stela-fg)',
        focusBackground: 'var(--stela-bg-2)',
        focusColor: 'var(--stela-fg-1)',
        activeBackground: 'var(--stela-bg-2)',
        activeColor: 'var(--stela-fg-1)',
        icon: {
          color: 'var(--stela-fg-2)',
          focusColor: 'var(--stela-gold)',
          activeColor: 'var(--stela-gold)',
        },
      },
      submenuIcon: {
        color: 'var(--stela-fg-3)',
        focusColor: 'var(--stela-gold)',
      },
      separator: {
        borderColor: 'var(--stela-line)',
      },
    },

    // --p-menu-* (popup + sidebar; NO menubar/megamenu/tieredmenu/
    // contextmenu, que quedan para el lote E): menu no tiene bloque
    // `colorScheme` en Lara (dist/lara/menu) — todo es de nivel superior.
    // `item` en Lara solo expone `focusBackground`/`color`/`focusColor` (y
    // `icon.*`) — no tiene `hoverBackground`, `hoverColor`,
    // `activeBackground` ni `activeColor` — así que
    // --p-menu-item-hover-background/-color y
    // --p-menu-item-active-background/-color no reproducían nada real: se
    // descartan (regla 6).
    menu: {
      root: {
        background: 'var(--stela-surface)',
        borderColor: 'var(--stela-line)',
        borderRadius: 'var(--stela-radius-md)',
        shadow: 'var(--stela-shadow-md)',
      },
      item: {
        color: 'var(--stela-fg-1)',
        focusBackground: 'var(--stela-bg)',
      },
      submenuLabel: {
        color: 'var(--stela-fg-3)',
      },
    },

    // --p-dialog-* / --p-popover-*: ninguno de los dos tiene bloque
    // `colorScheme` en Lara (dist/lara/dialog, dist/lara/popover) — todo es
    // de nivel superior — y el puente solo vivía en prime-overrides (theme.css
    // no los toca), así que un único valor basta. `--p-popover-background`
    // no existe en el puente (no se fijaba, se deja el valor de Lara).
    dialog: {
      root: {
        background: 'var(--stela-surface)',
        borderColor: 'var(--stela-line)',
        borderRadius: 'var(--stela-radius-lg)',
        shadow: 'var(--stela-shadow-lg)',
      },
      // 15px en cabecera, contenido y pie (antes con !important en theme.css).
      header: { padding: '15px' },
      content: { padding: '15px' },
      footer: { padding: '15px' },
    },
    popover: {
      root: {
        borderColor: 'var(--stela-line)',
        borderRadius: 'var(--stela-radius-md)',
        shadow: 'var(--stela-shadow-md)',
      },
    },

    // Lote E (Tarea 4, ITSM #1839381): resto del puente --p-*.
    // tag.root.padding/fontWeight son los de theme.css: ese fichero importa
    // theme-kit.css (que a su vez importa prime-overrides.css) y luego
    // redeclara las mismas variables en su propio :root, así que sus
    // valores pisan a los de prime-overrides (mismo patrón que en el lote
    // A). success/warn/danger/info también los redeclara theme.css y ganan
    // esos valores; info tiene además un override propio bajo
    // body.dark-theme (color distinto en oscuro). primary/secondary/
    // contrast solo viven en prime-overrides, sin conflicto.
    tag: {
      root: {
        borderRadius: 'var(--stela-radius-sm)',
        padding: '5px 10px',
        fontSize: 'var(--stela-font-size-sm)',
        fontWeight: '600',
      },
      icon: { size: 'var(--stela-font-size-sm)' },
      colorScheme: {
        light: {
          primary: { background: 'var(--stela-gold)', color: 'var(--stela-aubergine-dark)' },
          secondary: { background: 'var(--stela-bg-muted)', color: 'var(--stela-fg-2)' },
          success: { background: 'var(--stela-color-success)', color: '#fff' },
          info: {
            background: 'color-mix(in srgb, var(--stela-color-info) 20%, var(--stela-bg-surface))',
            color: 'var(--stela-text-secondary)',
          },
          warn: { background: 'var(--stela-color-warning)', color: '#fff' },
          danger: { background: 'var(--stela-color-danger)', color: '#fff' },
          contrast: { background: 'var(--stela-navy)', color: '#ffffff' },
        },
        dark: {
          primary: { background: 'var(--stela-gold)', color: 'var(--stela-aubergine-dark)' },
          // --p-tag-secondary-background tiene un override propio bajo
          // body.dark-theme que repite el mismo var(--stela-bg-muted) — se
          // deja explícito aquí porque el valor real cambia con el esquema.
          secondary: { background: 'var(--stela-bg-muted)', color: 'var(--stela-fg-2)' },
          success: { background: 'var(--stela-color-success)', color: '#fff' },
          // --p-tag-info-color tenía una declaración propia bajo
          // body.dark-theme en theme.css (var(--stela-color-info)), pero
          // verificado contra el build real (línea base) esa declaración
          // NUNCA ganaba: el valor computado en oscuro seguía siendo
          // var(--stela-text-secondary), el de :root (mismo patrón que
          // panel-header-background/tabs-tab-active-border-color en el
          // lote D). Se deja el mismo valor que en claro.
          info: {
            background: 'color-mix(in srgb, var(--stela-color-info) 20%, var(--stela-bg-surface))',
            color: 'var(--stela-text-secondary)',
          },
          warn: { background: 'var(--stela-color-warning)', color: '#fff' },
          danger: { background: 'var(--stela-color-danger)', color: '#fff' },
          contrast: { background: 'var(--stela-navy)', color: '#ffffff' },
        },
      },
    },

    // --p-chip-background tiene un override propio bajo body.dark-theme que
    // repite el mismo var(--stela-bg-muted) que en claro (ver comentario del
    // bloque "Recálculo en oscuro" en prime-overrides.css).
    chip: {
      root: { borderRadius: 'var(--stela-radius-pill)' },
      colorScheme: {
        light: { root: { background: 'var(--stela-bg-muted)', color: 'var(--stela-fg-1)' } },
        dark: { root: { background: 'var(--stela-bg-muted)', color: 'var(--stela-fg-1)' } },
      },
    },

    // El puente solo fija el `background` de cada severidad de badge, nunca
    // el `color` (se deja el que trae Lara).
    badge: {
      root: { borderRadius: 'var(--stela-radius-pill)' },
      colorScheme: {
        light: {
          primary: { background: 'var(--stela-gold)', color: 'var(--stela-aubergine-dark)' },
          success: { background: 'var(--stela-success)' },
          info: { background: 'var(--stela-info)' },
          warn: { background: 'var(--stela-warning)' },
          danger: { background: 'var(--stela-danger)' },
        },
        dark: {
          primary: { background: 'var(--stela-gold)', color: 'var(--stela-aubergine-dark)' },
          success: { background: 'var(--stela-success)' },
          info: { background: 'var(--stela-info)' },
          warn: { background: 'var(--stela-warning)' },
          danger: { background: 'var(--stela-danger)' },
        },
      },
    },

    // message.* es agnóstico de esquema en el puente (una sola declaración
    // en :root, sin override en body.dark-theme), así que se repite igual
    // en ambos esquemas.
    message: {
      root: { borderRadius: 'var(--stela-radius-md)' },
      colorScheme: {
        light: {
          success: {
            background: 'var(--stela-success-bg)',
            borderColor: 'var(--stela-success)',
            color: 'var(--stela-success)',
          },
          info: {
            background: 'var(--stela-info-bg)',
            borderColor: 'var(--stela-info)',
            color: 'var(--stela-info)',
          },
          warn: {
            background: 'var(--stela-warning-bg)',
            borderColor: 'var(--stela-warning)',
            color: '#8a6500',
          },
          error: {
            background: 'var(--stela-danger-bg)',
            borderColor: 'var(--stela-danger)',
            color: 'var(--stela-danger)',
          },
        },
        dark: {
          success: {
            background: 'var(--stela-success-bg)',
            borderColor: 'var(--stela-success)',
            color: 'var(--stela-success)',
          },
          info: {
            background: 'var(--stela-info-bg)',
            borderColor: 'var(--stela-info)',
            color: 'var(--stela-info)',
          },
          warn: {
            background: 'var(--stela-warning-bg)',
            borderColor: 'var(--stela-warning)',
            color: '#8a6500',
          },
          error: {
            background: 'var(--stela-danger-bg)',
            borderColor: 'var(--stela-danger)',
            color: 'var(--stela-danger)',
          },
        },
      },
    },

    // toast.* mimetiza la paleta de message (misma nota: agnóstico de
    // esquema). --p-toast-shadow se descarta: Lara no tiene ningún token
    // `toast.shadow` a nivel raíz (solo `<severity>.shadow`, que ya
    // referencia `{overlay.popover.shadow}`) y ningún selector CSS de
    // PrimeNG usa la variable --p-toast-shadow, así que nunca hacía nada
    // (regla 6). --p-toast-detail-font-size sí existe (`detail.fontSize`,
    // agnóstico, top-level).
    toast: {
      root: { borderRadius: 'var(--stela-radius-md)' },
      detail: { fontSize: '1.13rem' },
      colorScheme: {
        light: {
          success: {
            background: 'var(--stela-success-bg)',
            borderColor: 'var(--stela-success)',
            color: 'var(--stela-success)',
          },
          info: {
            background: 'var(--stela-info-bg)',
            borderColor: 'var(--stela-info)',
            color: 'var(--stela-info)',
          },
          warn: {
            background: 'var(--stela-warning-bg)',
            borderColor: 'var(--stela-warning)',
            color: '#8a6500',
          },
          error: {
            background: 'var(--stela-danger-bg)',
            borderColor: 'var(--stela-danger)',
            color: 'var(--stela-danger)',
          },
        },
        dark: {
          success: {
            background: 'var(--stela-success-bg)',
            borderColor: 'var(--stela-success)',
            color: 'var(--stela-success)',
          },
          info: {
            background: 'var(--stela-info-bg)',
            borderColor: 'var(--stela-info)',
            color: 'var(--stela-info)',
          },
          warn: {
            background: 'var(--stela-warning-bg)',
            borderColor: 'var(--stela-warning)',
            color: '#8a6500',
          },
          error: {
            background: 'var(--stela-danger-bg)',
            borderColor: 'var(--stela-danger)',
            color: 'var(--stela-danger)',
          },
        },
      },
    },

    // progressbar no tiene bloque `colorScheme` en Lara (dist/lara/progressbar):
    // root/value/label son todos de nivel superior. --p-progressbar-value-color
    // no tiene un token `value.color` en Lara — el color del texto dentro de
    // la barra es `label.color`, que es donde de verdad se pinta.
    // --p-progressbar-background tiene un override propio bajo
    // body.dark-theme que repite el mismo var(--stela-bg-muted).
    progressbar: {
      root: {
        background: 'var(--stela-bg-muted)',
        borderRadius: 'var(--stela-radius-pill)',
      },
      value: { background: 'var(--stela-gold)' },
      label: { color: 'var(--stela-aubergine-dark)' },
    },

    // skeleton.colorScheme.{light,dark}.root: --p-skeleton-background tiene
    // override propio bajo body.dark-theme (repite var(--stela-bg-muted));
    // --p-skeleton-animation-background es agnóstico (una sola declaración).
    skeleton: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--stela-bg-muted)',
            animationBackground: 'rgba(201, 168, 76, 0.08)',
          },
        },
        dark: {
          root: {
            background: 'var(--stela-bg-muted)',
            animationBackground: 'rgba(201, 168, 76, 0.08)',
          },
        },
      },
    },

    // divider no tiene bloque `colorScheme` en Lara (dist/lara/divider):
    // root.borderColor es de nivel superior.
    divider: {
      root: { borderColor: 'var(--stela-line)' },
    },

    // togglebutton (lote 7.4): Lara pinta el estado normal con los colores de campo (en oscuro el
    // texto queda casi sin contraste) y el marcado con `highlight`. Aquí el normal es superficie,
    // línea y texto secundario, y el marcado es dorado suave con borde dorado, en ambos esquemas.
    togglebutton: {
      root: {
        background: 'var(--stela-surface)',
        borderColor: 'var(--stela-line)',
        color: 'var(--stela-fg-2)',
        hoverColor: 'var(--stela-fg-1)',
        checkedBackground: 'color-mix(in srgb, var(--stela-gold) 16%, var(--stela-surface))',
        checkedBorderColor: 'var(--stela-gold)',
      },
      icon: {
        color: 'var(--stela-fg-3)',
        hoverColor: 'var(--stela-fg-2)',
      },
      colorScheme: {
        light: {
          root: { hoverBackground: 'var(--stela-bg-muted)', checkedColor: 'var(--stela-gold-dark)' },
          icon: { checkedColor: 'var(--stela-gold-dark)' },
        },
        dark: {
          root: { hoverBackground: 'var(--stela-bg-muted)', checkedColor: 'var(--stela-gold)' },
          icon: { checkedColor: 'var(--stela-gold)' },
        },
      },
    },
  },
  css: STELA_PRESET_CSS,
}, stelaDarkScheme);
